import React from 'react'

import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { ListNode, ListItemNode } from '@lexical/list'
import type { InitialConfigType } from '@lexical/react/LexicalComposer'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode } from '@lexical/rich-text'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import { $getRoot, type EditorState, type LexicalEditor } from 'lexical'

import { ToolbarPlugin } from './components/ToolbarPlugin'
import { editorTheme } from './config/editorTheme'
import { AutoLinkPlugin } from './plugins/AutoLinkPlugin'

// Types
type EditorConfig = {
  enableHeadings?: boolean
  enableFontSize?: boolean
  enableBold?: boolean
  enableItalic?: boolean
  enableUnderline?: boolean
  enableLists?: boolean
  enableLinks?: boolean
  enableAlignment?: boolean
  enableFontColor?: boolean
  enableHighlight?: boolean
  enableTextTransform?: boolean
}

type RichTextEditorProps = {
  config?: EditorConfig
  onChange?: (html: string, editorState: EditorState) => void
  initialHtml?: string
}

const defaultConfig: EditorConfig = {
  enableHeadings: true,
  enableFontSize: true,
  enableBold: true,
  enableItalic: true,
  enableUnderline: true,
  enableLists: true,
  enableLinks: true,
  enableAlignment: true,
  enableFontColor: true,
  enableHighlight: true,
  enableTextTransform: true,
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  config = defaultConfig,
  onChange,
  initialHtml,
}) => {
  const mergedConfig = { ...defaultConfig, ...config }

  const initialConfig: InitialConfigType = {
    namespace: 'RichTextEditor',
    theme: editorTheme,
    nodes: [HeadingNode, LinkNode, AutoLinkNode, ListNode, ListItemNode],
    onError: (error: Error) => {
      // eslint-disable-next-line no-console
      console.error('Lexical error:', error)
    },
    editorState: initialHtml
      ? editor => {
          // Added this section
          const parser = new DOMParser()
          const dom = parser.parseFromString(initialHtml, 'text/html')
          const nodes = $generateNodesFromDOM(editor, dom)
          $getRoot().append(...nodes)
        }
      : undefined,
  }

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    if (onChange) {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor)
        onChange(sanitizeAndInsertTargetBlank(html), editorState)
      })
    }
  }

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="editor-wrapper overflow-hidden rounded-lg border border-gray-300">
        <ToolbarPlugin config={mergedConfig} />
        <div className="editor-container relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="min-h-[300px] rounded-b-lg border border-solid border-gray-300 focus:outline-none" />
            }
            placeholder={
              <div className="editor-placeholder pointer-events-none absolute left-4 top-4 text-gray-400">
                Enter some text...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={handleChange} />
          <HistoryPlugin />
          {mergedConfig.enableLists && <ListPlugin />}
          {mergedConfig.enableLinks && (
            <>
              <LinkPlugin />
              <AutoLinkPlugin />
              {/* <LinkTargetPlugin /> */}
            </>
          )}
        </div>
      </div>
    </LexicalComposer>
  )
}
